/*/
	Calendar Loader
	Stephen Ginn at Crema Design Studio
	v2025-01-29
	
	This script generates a calendar and/or event list using FullCalendar
	and the provided calendar url. Clicked events will open a Bootstrap
	modal with the event details.
	
	Cors Bypass:
	https://clients.cremadesignstudio.com/cors-bypass.php?url=
/*/

// Utility function to create a hash from a string
const createHash = (str) => {
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		const char = str.charCodeAt(i);
		hash = (hash << 5) - hash + char;
		hash |= 0; // Convert to 32bit integer
	}
	return hash.toString(36);
};

// Utility function to perform a deep merge of objects
const deepMerge = (target, source) => {
	for (const key in source) {
		if (source[key] instanceof Object && key in target) {
			target[key] = deepMerge(target[key], source[key]);
		} else {
			target[key] = source[key];
		}
	}
	return target;
}

// Utility function to replace placeholders in templates
const replacePlaceholders = (template, placeholders) => {
	for (const [key, value] of Object.entries(placeholders)) {
		template = template.replace(new RegExp(key, 'g'), value);
	}
	return template;
}

// Utility function to parse markdown
const parseMarkdown = markdown => {
	const rules = [
		{ regex: /(\r\n|\r|\n){2,}/g, replacement: '\n' },
		{ regex: /[*-]{3,}/g, replacement: '---' },
		{ regex: /^### (.*$)/gim, replacement: '<h3>$1</h3>' },
		{ regex: /^## (.*$)/gim, replacement: '<h2>$1</h2>' },
		{ regex: /^# (.*$)/gim, replacement: '<h1>$1</h1>' },
		{ regex: /\*\*(.*)\*\*/gim, replacement: '<b>$1</b>' },
		{ regex: /__(.*)__/gim, replacement: '<b>$1</b>' },
		{ regex: /\*(.*)\*/gim, replacement: '<i>$1</i>' },
		{ regex: /_(.*)_/gim, replacement: '<i>$1</i>' },
		{ regex: /\[(.*?)\]\((.*?)\)/gim, replacement: '<a href="$2">$1</a>' },
		{ regex: /---/gim, replacement: '<hr>' }
	];
	
	rules.forEach(rule => {
		markdown = markdown.replace(rule.regex, rule.replacement);
	});
	
	return markdown.trim();
}

FullCalendar.init = (options) => {
	const defaults = {
		title: "Events",
		viewType: 'calendar',
		templates: {
			eventView: `
				<header>
					<h2 class="title">{title}</h2>
				</header>
				<div class="event-body events" id="event-wrap">
					{eventsHTML}
				</div>
				<div class="event-footer">
					<a class="btn btn-lg btn-block btn-outline-secondary" href="/pages/events">View Full Calendar</a>
				</div>`,
			event: `
				<div class="event">
					<span class="date"><small>{month}</small>{day}</span>
					<div class="details">
						<h5 class="mb-1 event-title">{title}</h5>
						<div>{time}</div>
						{button}
					</div>
				</div>`,
			eventButton: `
				<a href="{url}"><i class="fas fa-envelope-open-text"></i> More Info</a>`,
			modal: `
				<div class="modal fade" id="{id}" tabindex="-1" aria-hidden="true">
					<div class="modal-dialog modal-lg">
						<div class="modal-content">
							<div class="modal-header">
								<h4 class="modal-title">{title}</h4>
								<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
							</div>
							<div class="modal-body py-4">
								<div class="lead time-date mb-4">
									<div class="date">{date}</div>
									<div class="time">{time}</div>
								</div>
								<div class="location mb-4">
									<h5>Location</h5>
									<div class="text">{location}</div>
								</div>
								<div class="description">
									<h5>Description</h5>
									<div class="text">{description}</div>
								</div>
							</div>
						</div>
					</div>
				</div>`
		}
	};
	
	// Merge default options with user-provided options
	options = deepMerge(defaults, options);
	
	// This function transforms the event data into a more usable format
	const transformEventData = (fcEvent, listView = false) => {
		const def = listView ? fcEvent.def : fcEvent._def;
		const instance = listView ? fcEvent.instance : fcEvent._instance;
		const { start, end } = instance.range;
	
		const momentStart = moment(start).tz("GMT");
		const momentEnd = moment(end).tz("GMT");
	
		return {
			id: def.publicId || createHash(`${start}-${def.title}`),
			allDay: def.allDay,
			title: def.title,
			url: def.url,
			location: def.extendedProps.location || '',
			description: parseMarkdown(def.extendedProps.description || ''),
			attachments: def.extendedProps.attachments,
			start,
			end,
			strings: {
				date_long: momentStart.format("MMMM DD"),
				date: momentStart.format(listView ? "MMM DD" : "dddd, MMMM DD, YYYY"),
				start: momentStart.format("h:mma"),
				end: momentEnd.format("h:mma"),
				range: `${momentStart.format("h:mma")} – ${momentEnd.format("h:mma")} CST`
			}
		};
	};	
	
	// Remove inline styles from events list...
	const removeCalendarHeight = (e, functionName) => {
		let viewHarness = e.view.calendar.el.querySelector(".fc-view-harness");
		
		if (e.view.type == "eventView") {
			viewHarness.removeAttribute("style");
		}
		
		if (functionName === "viewDidMount") {
			if (e.view.type === "eventView") {
				viewHarness.classList.add("h-auto");
			} else {
				viewHarness.classList.remove("h-auto");
			}
		}
	}
	
	const calendarEl = document.querySelector(options.selector);

	// Custom hook fired when FullCalendar finishes loading data.
	// Kept off calendarOptions so FC's option validator does not warn about it.
	const loaded = (e) => {
		removeCalendarHeight(e, "loaded");

		if (typeof facebookPlugin !== 'undefined') {
			facebookPlugin.refreshIframe();
		}
	};

	const calendarOptions = {
		themeSystem: 'bootstrap5',
		nowIndicator: true,
		slotLabelFormat: {
			hour: "numeric",
			minute: "2-digit",
			omitZeroMinute: true,
			meridiem: "short",
		},
		events: {
			url: encodeURI(options.url),
			format: 'ics'
		},
		eventDataTransform: eventData => {
			// Add an ID to each event if it doesn't already have one
			if (!eventData.id) {
				eventData.id = createHash(`${eventData.start}-${eventData.title}`);
			}
			
			return eventData;
		},
		views: {
			dayGridMonth: {},
			timeGridWeek: {
				titleFormat: {
					year: 'numeric',
					month: 'short'
				},
				dayHeaderContent: arg => {
					const date = calendar.formatDate(arg.date, { weekday: "short", day: "numeric" }).split(' ');
					
					return {
						html: `<h3 class="lh-1 mt-2 mb-0">
							<small class="d-block text-uppercase fw-normal">${date[1]}</small>
							${date[0]}
						</h3>`
					}
				}
			},
			eventView: {
				aspectRatio: 2.125,
				classNames: ['fc-event-view'],
				duration: { days: 90 },
				content: props => {
					const events = FullCalendar.sliceEvents(props, true).slice(0, 5);
					const eventsContainer = document.createElement('div');
					let eventsHTML = `<div id="fc-no-events" class="alert alert-info">
						No events planned in the selected period.
					</div>`;
					
					if (events.length > 0) {
						eventsHTML = events.map(event => {
							event = transformEventData({
								instance: event.instance,
								def: event.def
							}, true);
							
							const [month, day] = event.strings.date.split(' ');
							const btnMarkup = replacePlaceholders(options.templates.eventButton, {
								'{url}': event.url || '#'
							});
							
							const eventHtml = replacePlaceholders(options.templates.event, {
								'{title}': event.title,
								'{month}': month,
								'{day}': day,
								'{date}': event.strings.date,
								'{date:long}': event.strings.date_long,
								'{time}': event.strings.range,
								'{button}': btnMarkup
							});
							
							const tempDom = new DOMParser().parseFromString(eventHtml, 'text/html');
							const eventEl = tempDom.body.firstChild;
							
							eventEl.id = event.id;
							eventEl.className = 'custom-event';
							eventEl.innerHTML = eventHtml;
							
							return eventEl.outerHTML;
						}).join('');
					}
					
					eventsContainer.innerHTML = replacePlaceholders(options.templates.eventView, {
						'{title}': options.title,
						'{eventsHTML}': eventsHTML
					});
					
					eventsContainer.querySelectorAll('.custom-event a').forEach(link => {
						link.href = "#";
						link.classList.add('fw-bold');
						
						link.addEventListener('click', e => {
							const eventID = e.target.closest('.custom-event').id;
							const event = calendar.getEventById(eventID);
							
							calendarOptions.eventClick({
								jsEvent: e,
								el: e.target,
								event: {
									_instance: event._instance,
									_def: event._def
								},
								view: calendar.view
							});
						});
					});
					
					return { domNodes: [eventsContainer] };
				}
			}
		},
		customButtons: {
			eventViewButton: {
				text: 'events',
				click: (e, el) => {
					calendar.changeView('eventView');
				}
			}
		},
		eventClick: info => {
			const modalId = `modal-event-${info.event._def.publicId}`;
			let modalEl = document.querySelector(`#${modalId}`);
			
			// Add event modal if it doesn't exist
			if (!modalEl) {
				const event = transformEventData(info.event);
				const template = replacePlaceholders(options.templates.modal, {
					'{id}': modalId,
					'{title}': event.title,
					'{date}': event.strings.date,
					'{time}': event.allDay ? "All Day Event" : event.strings.range,
					'{location}': event.location || "No location available",
					'{description}': event.description || "No description available"
				});
				
				document.body.insertAdjacentHTML('beforeend', template);
				modalEl = document.getElementById(modalId);
			}
			
			info.jsEvent.preventDefault(); // Prevent page jump
			new bootstrap.Modal(modalEl).show(); // Show modal
		},
		loading: isLoading => !isLoading && loaded(calendar)
	};

	["viewDidMount", "datesSet", "windowResize"].forEach(eventName => {
		calendarOptions[eventName] = (e) => {
			removeCalendarHeight(e, eventName);
		}
	});
	
	switch (options.viewType) {
		case 'calendar':
			Object.assign(calendarOptions, {
				headerToolbar: {
					left: 'prev,next today',
					center: 'title',
					right: 'dayGridMonth,timeGridWeek,timeGridDay,eventViewButton'
				},
				dayMaxEventRows: 2,
				dayMaxEvents: true
			});
		break;
		
		case 'list':
			Object.assign(calendarOptions, {
				headerToolbar: '',
				initialView: 'eventView',
				handleWindowResize: true
			});
		break;
		
		default:
			console.warn(`Sorry, we don't recognize that ${options.viewType}.`);
	}
	
	window.calendar = new FullCalendar.Calendar(calendarEl, calendarOptions);
	window.calendar.render();
}
